import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Lock, Crown, ArrowLeft, Loader2, Play, Eye } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { MediaPlayer } from "@/components/MediaPlayer";

export default function Watch() {
  const { id } = useParams();
  const { isSubscribed } = useAuth();
  const nav = useNavigate();
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subOpen, setSubOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [source, setSource] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSource(null);
    endpoints.show(id).then((r) => setShow(r.data)).catch(() => setShow(null)).finally(() => setLoading(false));
  }, [id]);

  const openSub = async () => {
    try {
      const { data } = await endpoints.plans();
      setPlan(data.find((p) => p.tier === "premium" && p.period === "monthly") || data[0]);
      setSubOpen(true);
    } catch {}
  };

  // Entitlement is verified on the BACKEND before a playable URL is returned.
  const startPlayback = async () => {
    setStarting(true);
    try {
      const { data } = await endpoints.showPlayback(id);
      setSource(data.playback);
      endpoints.registerView(id).then((r) => {
        if (typeof r?.data?.views === "number") setShow((s) => (s ? { ...s, views: r.data.views } : s));
      }).catch(() => {});
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) { toast.error("Subscribe to watch this show."); openSub(); }
      else toast.error(apiError(e?.response?.data?.detail));
    } finally { setStarting(false); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!show) return <div className="text-center py-32 text-slate-400">Show not found. <Link to="/vod" className="text-[#E11D2A]">Back to library</Link></div>;

  const locked = !isSubscribed;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto py-8">
      <button onClick={() => nav(-1)} className="text-slate-400 hover:text-white flex items-center gap-1.5 text-sm mb-5"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="rounded-2xl overflow-hidden border border-[#273042] bg-black">
        {locked ? (
          <div className="aspect-video bg-[#121620] flex flex-col items-center justify-center text-center p-8" data-testid="watch-paywall">
            <span className="w-16 h-16 rounded-2xl bg-[#2563EB]/15 flex items-center justify-center mb-4"><Lock className="w-8 h-8 text-[#2563EB]" /></span>
            <div className="inline-flex items-center gap-1.5 text-[#2563EB] text-xs font-bold uppercase tracking-wider"><Crown className="w-4 h-4" /> Premium content</div>
            <h2 className="text-2xl font-bold text-white mt-3">Subscribe to watch “{show.title}”</h2>
            <p className="text-slate-400 text-sm mt-2 max-w-md">This is a members-only show. Unlock unlimited premium shows, ad-free radio and live events.</p>
            <button onClick={openSub} data-testid="watch-subscribe-btn" className="mt-6 h-12 px-7 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow">Become a member</button>
          </div>
        ) : source ? (
          <MediaPlayer source={source} title={show.title} testid="watch-player" />
        ) : (
          <div className="aspect-video bg-[#0A0C10] flex flex-col items-center justify-center text-center p-8" data-testid="watch-start">
            <img src={show.thumbnail || show.coverUrl || "/bb-logo.png"} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <button onClick={startPlayback} disabled={starting} data-testid="watch-play-btn"
              className="relative z-10 w-16 h-16 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] flex items-center justify-center btn-glow disabled:opacity-60">
              {starting ? <Loader2 className="w-7 h-7 text-white animate-spin" /> : <Play className="w-7 h-7 text-white ml-1" />}
            </button>
            <div className="relative z-10 text-white font-semibold mt-4">{starting ? "Starting…" : "Play"}</div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-[#E11D2A] font-bold">{show.category}</div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1.5">{show.title}</h1>
        <div className="flex items-center gap-3 mt-1.5">
          {show.duration && <span className="text-sm text-slate-400 font-mono">{show.duration}</span>}
          {show.views > 0 && <span className="flex items-center gap-1 text-sm text-slate-400 font-semibold" data-testid="watch-views"><Eye className="w-4 h-4" /> {show.views} view{show.views === 1 ? "" : "s"}</span>}
        </div>
        {show.description && <p className="text-slate-300 mt-4 leading-relaxed max-w-3xl">{show.description}</p>}
      </div>

      <SubscribeDialog plan={plan} open={subOpen} onClose={() => setSubOpen(false)} />
    </div>
  );
}
